import { api } from "../App";
import { toast } from "sonner";

/**
 * Open an attachment URL — handles both external links and our own
 * auth-gated file endpoints.
 *
 * Two cases:
 * 1. External (http/https) — trivial `window.open`.
 * 2. Our own server-hosted files (relative paths like `/api/pitch-decks/file/…`
 *    or `/api/bizdev/briefs/.../attachments/.../download`) — those endpoints
 *    are auth-gated via `Depends(get_current_user)`. A plain `window.open` /
 *    `<a href>` navigation only carries cookies; users on the JWT auth path
 *    (localStorage `dreamteam_token`) get a 401. So we fetch the file through
 *    the authenticated axios instance as a blob, then trigger open/download
 *    via an anchor click on the object URL.
 *
 * `filename` is passed through so browsers that treat the file as a download
 * (Word / Excel / PowerPoint) get the right "Save As" name.
 */
export async function openAttachmentUrl(rawUrl, filename) {
  const url = (rawUrl || "").trim();
  if (!url) return;

  // External URL — open directly
  if (/^https?:\/\//i.test(url)) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  // Relative (own backend). Axios baseURL already ends in `/api`, so strip the
  // leading `/api` off the stored URL before handing it to the instance. If the
  // URL doesn't start with `/api`, pass it through as-is.
  if (url.startsWith("/")) {
    try {
      const path = url.startsWith("/api") ? url.replace(/^\/api/, "") : url;
      const res = await api.get(path, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      if (filename) a.download = filename; // hint for "save as"; ignored for inline-previewable types
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke later so the new tab / download stream finishes first
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      console.error("Couldn't open attachment", e);
      // Surface the server's detail message when available — especially for
      // the 410 "file was lost in a redeploy, please re-upload" case. Axios
      // wraps error responses into e.response; when responseType was "blob"
      // the error body is a Blob we have to read as text.
      let detail = null;
      if (e.response?.data) {
        try {
          const maybeBlob = e.response.data;
          if (maybeBlob instanceof Blob) {
            const text = await maybeBlob.text();
            try { detail = JSON.parse(text)?.detail; } catch { detail = text; }
          } else if (typeof maybeBlob === "object") {
            detail = maybeBlob.detail;
          } else if (typeof maybeBlob === "string") {
            detail = maybeBlob;
          }
        } catch { /* best-effort parsing */ }
      }
      const status = e.response?.status;
      if (status === 410 && detail) {
        // 410 Gone — the backend already wrote a friendly message telling the
        // user what to do (usually "re-upload this file"). Longer duration
        // because this message actually requires reading.
        toast.error(detail, { duration: 8000 });
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("Couldn't open file — please try again");
      }
    }
    return;
  }

  // Unknown shape — best effort
  window.open(url, "_blank", "noopener,noreferrer");
}
